import { useEffect, useState } from 'react';
import { History, Loader2, X } from 'lucide-react';
import { useGetPromoCodeRedemptionsQuery, useUpdatePromoCodeMutation } from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { PromoCode, UpdatePromoCodeRequest } from '@/types';
import { isExhausted, promoCap, promoUsed } from './usage';

type LimitMode = 'single' | 'limited' | 'unlimited';

const capToMode = (cap: number | null): LimitMode => {
    if (cap === null) return 'unlimited';
    return cap === 1 ? 'single' : 'limited';
};

const toDateInput = (iso?: string | null) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

export default function EditPromoModal({ promo, onClose }: { promo: PromoCode | null; onClose: () => void }) {
    const [updatePromo, { isLoading }] = useUpdatePromoCodeMutation();

    const [mode, setMode] = useState<LimitMode>('single');
    const [limit, setLimit] = useState('50');
    const [oncePerUser, setOncePerUser] = useState(true);
    const [expiresAt, setExpiresAt] = useState('');
    const [reactivate, setReactivate] = useState(false);
    const [error, setError] = useState('');

    // Only fetch history once the drawer is actually open for a code.
    const { data: redemptions = [], isLoading: loadingHistory } = useGetPromoCodeRedemptionsQuery(promo?.id ?? '', {
        skip: !promo,
    });

    useEffect(() => {
        if (!promo) return;
        const cap = promoCap(promo);
        setMode(capToMode(cap));
        setLimit(cap && cap > 1 ? String(cap) : '50');
        setOncePerUser(promo.oncePerUser ?? true);
        setExpiresAt(toDateInput(promo.expiresAt));
        setReactivate(false);
        setError('');
    }, [promo]);

    useEffect(() => {
        if (!promo) return;
        const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        const previous = document.body.style.overflow;
        document.addEventListener('keydown', onKeyDown);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previous;
        };
    }, [promo, onClose]);

    if (!promo) return null;

    const used = promoUsed(promo);
    const exhausted = isExhausted(promo);

    // The API rejects a cap below what's already been redeemed rather than
    // truncating, so catch it here and say why instead of surfacing a 400.
    const nextCap = mode === 'unlimited' ? null : mode === 'single' ? 1 : Number(limit);
    const capTooLow = nextCap !== null && Number.isFinite(nextCap) && nextCap < used;
    // Raising the cap on an exhausted code reactivates it server-side, so the
    // admin never has to know about the reactivate flag for that case.
    const willAutoReactivate = exhausted && (nextCap === null || (Number.isFinite(nextCap) && nextCap > used));

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError('');

        if (mode === 'limited') {
            if (!Number.isInteger(nextCap) || (nextCap as number) < 1) {
                return setError('Enter a whole number of redemptions (1 or more).');
            }
            if (capTooLow) {
                return setError(`This code has already been redeemed ${used} times. The cap can't go below that.`);
            }
        }

        const data: UpdatePromoCodeRequest = {
            maxRedemptions: nextCap,
            oncePerUser,
            expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        };
        // Only sent when it actually does something — the implicit reactivation
        // from raising the cap covers the common case on its own.
        if (reactivate && !willAutoReactivate) data.reactivate = true;

        try {
            await updatePromo({ id: promo.id, data }).unwrap();
            onClose();
        } catch (err) {
            setError(parseErrorMessage(err, "Couldn't update this promo code."));
        }
    };

    const fmt = (d?: string | null) => (d ? new Date(d).toLocaleString('en-GB') : '—');

    return (
        <div
            className="fixed inset-0 z-50 flex justify-end bg-black/30"
            role="dialog"
            aria-modal="true"
            aria-label={`Edit promo code ${promo.code}`}
            onClick={onClose}
        >
            <div
                className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-start justify-between gap-3 border-b border-gray-100 p-5">
                    <div className="min-w-0">
                        <h2 className="truncate text-lg font-semibold text-gray-900">{promo.code}</h2>
                        <p className="mt-0.5 text-sm text-gray-500">
                            {used} redeemed
                            {exhausted && <span className="ml-1.5 font-medium text-amber-600">· exhausted</span>}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </header>

                <form onSubmit={handleSubmit} className="flex flex-col gap-5 border-b border-gray-100 p-5">
                    <fieldset>
                        <legend className="mb-2 text-xs font-medium tracking-wide text-gray-500 uppercase">
                            Redemption limit
                        </legend>
                        <div className="flex flex-col gap-2">
                            {(
                                [
                                    ['single', 'Single use'],
                                    ['limited', 'Limited number of users'],
                                    ['unlimited', 'Unlimited'],
                                ] as [LimitMode, string][]
                            ).map(([value, label]) => (
                                <label key={value} className="flex items-center gap-2.5 text-sm text-gray-700">
                                    <input
                                        type="radio"
                                        name="limit-mode"
                                        checked={mode === value}
                                        onChange={() => setMode(value)}
                                        className="h-4 w-4 accent-[#FF6B35]"
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>

                        {mode === 'limited' && (
                            <div className="mt-3">
                                <input
                                    type="number"
                                    min={Math.max(1, used)}
                                    step={1}
                                    value={limit}
                                    onChange={(e) => setLimit(e.target.value)}
                                    aria-label="Maximum redemptions"
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-[#FF6B35] focus:outline-none"
                                />
                                {capTooLow ? (
                                    <p className="mt-1.5 text-xs text-red-600">
                                        Already redeemed {used} times — the cap can't go below that.
                                    </p>
                                ) : (
                                    <p className="mt-1.5 text-xs text-gray-500">Minimum {Math.max(1, used)}.</p>
                                )}
                            </div>
                        )}

                        {willAutoReactivate && (
                            <p className="mt-3 rounded-lg bg-green-50 px-3 py-2 text-xs text-green-700">
                                Raising the limit will reactivate this code automatically.
                            </p>
                        )}
                    </fieldset>

                    <label className="flex items-start gap-2.5 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={oncePerUser}
                            onChange={(e) => setOncePerUser(e.target.checked)}
                            className="mt-0.5 h-4 w-4 accent-[#FF6B35]"
                        />
                        <span>
                            One redemption per user
                            <span className="block text-xs text-gray-500">
                                Stops a single person draining a multi-use code.
                            </span>
                        </span>
                    </label>

                    <div>
                        <label
                            htmlFor="promo-expires"
                            className="mb-1.5 block text-xs font-medium tracking-wide text-gray-500 uppercase"
                        >
                            Expires
                        </label>
                        <input
                            id="promo-expires"
                            type="date"
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-[#FF6B35] focus:outline-none"
                        />
                        <p className="mt-1.5 text-xs text-gray-500">Leave blank for no expiry.</p>
                    </div>

                    {/* Only meaningful when the raise-the-cap path won't already do it. */}
                    {promo.status !== 'active' && !willAutoReactivate && (
                        <label className="flex items-center gap-2.5 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={reactivate}
                                onChange={(e) => setReactivate(e.target.checked)}
                                className="h-4 w-4 accent-[#FF6B35]"
                            />
                            Reactivate this code
                        </label>
                    )}

                    {error && (
                        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                            {error}
                        </p>
                    )}

                    <div className="flex gap-2">
                        <button
                            type="submit"
                            disabled={isLoading || capTooLow}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#FF6B35] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#F05A22] disabled:opacity-60"
                        >
                            {isLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                            Save changes
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>

                <section className="p-5">
                    <h3 className="mb-3 flex items-center gap-2 text-xs font-medium tracking-wide text-gray-500 uppercase">
                        <History className="h-3.5 w-3.5" aria-hidden="true" />
                        Redemption history
                    </h3>
                    {loadingHistory ? (
                        <div className="flex justify-center py-6">
                            <Loader2 className="h-5 w-5 animate-spin text-gray-400" aria-hidden="true" />
                        </div>
                    ) : redemptions.length === 0 ? (
                        <p className="py-4 text-sm text-gray-500">Not redeemed yet.</p>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {redemptions.map((entry) => (
                                <li key={entry.id} className="py-2.5">
                                    <p className="truncate text-sm text-gray-900">
                                        {entry.userEmail || 'Unknown user'}
                                    </p>
                                    <p className="text-xs text-gray-500">{fmt(entry.redeemedAt)}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
}
